import { useState, useRef, useEffect } from 'react';
import { useSystemSettings } from '../../hooks/useSystemSettings';
import { useOnboardingTour } from '../../contexts/OnboardingTourContext';

const TUTORIAL_TOPICS = [
  { icon: 'ri-bank-line',             text: 'Como cadastrar contas bancárias' },
  { icon: 'ri-exchange-dollar-line',  text: 'Como lançar receitas e despesas' },
  { icon: 'ri-focus-3-line',          text: 'Como acompanhar metas financeiras' },
  { icon: 'ri-bar-chart-2-line',      text: 'Como usar relatórios e insights' },
  { icon: 'ri-bank-card-line',        text: 'Como organizar cartões e faturas' },
];

/**
 * HelpFAB — Floating Action Button de suporte + tutorial na área do cliente.
 * O botão de ajuda é sempre visível. Os canais de suporte aparecem apenas
 * quando configurados em Configurações do Sistema.
 */
export const HelpFAB = () => {
  const { settings } = useSystemSettings();
  const [open, setOpen]         = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { restartTour } = useOnboardingTour();

  const hasEmail    = !!settings.support_email;
  const hasWhatsApp = !!settings.whatsapp;
  const hasHelp     = !!settings.help_url;
  const platformName = settings.platform_name || 'Suporte';
  const appPrimary   = settings.app_primary_color || '#10b981';

  // Close popup on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close tutorial modal on ESC
  useEffect(() => {
    if (!showTutorial) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowTutorial(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showTutorial]);

  const whatsappNumber = (settings.whatsapp || '').replace(/\D/g, '');
  const whatsappUrl = whatsappNumber
    ? `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(`Olá! Preciso de ajuda com o ${platformName}.`)}`
    : '';

  const handleStartTour = () => {
    setShowTutorial(false);
    setOpen(false);
    restartTour();
  };

  return (
    <>
      {/* ── Tutorial Modal ──────────────────────────────────────────────── */}
      {showTutorial && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowTutorial(false)}
          />

          {/* Modal card */}
          <div className="relative bg-white rounded-2xl w-full max-w-[420px] overflow-hidden shadow-2xl">
            {/* Header */}
            <div
              className="px-6 py-5 flex items-center gap-4"
              style={{ background: `linear-gradient(135deg, ${appPrimary}15 0%, ${appPrimary}05 100%)` }}
            >
              <div
                className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: appPrimary }}
              >
                <i className="ri-book-open-line text-white text-lg" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-[16px] font-800 text-gray-900">Tutorial do PandaPRO</h2>
                <p className="text-[12px] text-gray-500 mt-0.5">Aprenda os principais recursos em minutos</p>
              </div>
              <button
                onClick={() => setShowTutorial(false)}
                className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-black/5 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer shrink-0"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            {/* Topics list */}
            <div className="px-6 py-4 space-y-2">
              <p className="text-[11px] font-700 text-gray-400 uppercase tracking-wide mb-3">
                O que você vai aprender
              </p>
              {TUTORIAL_TOPICS.map((topic, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-gray-50"
                >
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${appPrimary}18` }}
                  >
                    <i className={`${topic.icon} text-sm`} style={{ color: appPrimary }} />
                  </div>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span
                      className="text-[11px] font-700 w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-white"
                      style={{ backgroundColor: appPrimary }}
                    >
                      {i + 1}
                    </span>
                    <p className="text-[13px] font-500 text-gray-700">{topic.text}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="px-6 pb-6 pt-2 flex items-center gap-3">
              <button
                onClick={() => setShowTutorial(false)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-600 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
              >
                Fechar
              </button>
              <button
                onClick={handleStartTour}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 text-white text-sm font-700 rounded-xl cursor-pointer whitespace-nowrap transition-all active:scale-95"
                style={{ backgroundColor: appPrimary }}
              >
                <i className="ri-play-circle-line" />
                Começar tour
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── FAB + Popup ─────────────────────────────────────────────────── */}
      <div ref={ref} className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        {/* Popup */}
        {open && (
          <div
            className="w-[280px] bg-white rounded-2xl border border-gray-100 overflow-hidden"
            style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }}
          >
            {/* Header */}
            <div
              className="px-4 py-4"
              style={{ background: `linear-gradient(135deg, ${appPrimary}18 0%, ${appPrimary}08 100%)` }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: appPrimary }}
                >
                  <i className="ri-customer-service-2-line text-white text-base" />
                </div>
                <div>
                  <p className="text-[14px] font-700 text-gray-900">Precisa de ajuda?</p>
                  <p className="text-[11px] text-gray-500 leading-tight">Estamos aqui para te apoiar</p>
                </div>
              </div>
            </div>

            {/* Options */}
            <div className="p-3 space-y-1">
              {/* Tutorial do sistema — always visible */}
              <button
                onClick={() => { setOpen(false); setShowTutorial(true); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer group"
              >
                <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 rounded-lg shrink-0">
                  <i className="ri-book-open-line text-emerald-600 text-base" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-[13px] font-600 text-gray-800">Tutorial do sistema</p>
                  <p className="text-[11px] text-gray-400">Aprenda a usar o PandaPRO passo a passo</p>
                </div>
                <i className="ri-arrow-right-s-line text-gray-300 text-base group-hover:text-gray-500 transition-colors" />
              </button>

              {(hasWhatsApp || hasEmail || hasHelp) && (
                <div className="border-t border-gray-50 my-1 pt-1" />
              )}

              {hasWhatsApp && (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer group"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-lg shrink-0">
                    <i className="ri-whatsapp-line text-green-600 text-base" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-600 text-gray-800">WhatsApp</p>
                    <p className="text-[11px] text-gray-400 truncate">{settings.whatsapp}</p>
                  </div>
                  <i className="ri-external-link-line text-gray-300 text-sm group-hover:text-gray-500 transition-colors" />
                </a>
              )}

              {hasEmail && (
                <a
                  href={`mailto:${settings.support_email}?subject=Suporte ${platformName}`}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer group"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-sky-100 rounded-lg shrink-0">
                    <i className="ri-mail-send-line text-sky-600 text-base" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-600 text-gray-800">E-mail de suporte</p>
                    <p className="text-[11px] text-gray-400 truncate">{settings.support_email}</p>
                  </div>
                  <i className="ri-external-link-line text-gray-300 text-sm group-hover:text-gray-500 transition-colors" />
                </a>
              )}

              {hasHelp && (
                <a
                  href={settings.help_url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer group"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                    <i className="ri-book-open-line text-amber-600 text-base" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-600 text-gray-800">Central de Ajuda</p>
                    <p className="text-[11px] text-gray-400 truncate">{settings.help_url}</p>
                  </div>
                  <i className="ri-external-link-line text-gray-300 text-sm group-hover:text-gray-500 transition-colors" />
                </a>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-gray-50">
              <p className="text-[11px] text-gray-400 text-center">
                Suporte oferecido por <span className="font-600 text-gray-600">{platformName}</span>
              </p>
            </div>
          </div>
        )}

        {/* FAB Button */}
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-12 h-12 rounded-full text-white flex items-center justify-center transition-all duration-200 cursor-pointer whitespace-nowrap shrink-0"
          style={{
            backgroundColor: appPrimary,
            transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
          }}
          title={open ? 'Fechar' : 'Ajuda & Suporte'}
        >
          <i className={`${open ? 'ri-close-line' : 'ri-question-line'} text-xl`} />
        </button>
      </div>
    </>
  );
};
